/**
 * reconnect-account.tsx — Báo tài khoản kênh mất kết nối + nút "Kết nối lại".
 *
 * Dùng ở hai chỗ: bộ chọn tài khoản kênh (filter-rail) và dải cảnh báo trên ô
 * soạn tin (chat-panel). Trước đây tài khoản rớt mà màn hình vẫn báo "Đã kết
 * nối", nhân viên cứ gửi và tin chỉ nằm trong CRM, khách không nhận được.
 *
 * Kết nối lại:
 *   - Zalo cá nhân rớt mạng → dựng lại bằng phiên đã lưu, KHÔNG phải quét QR.
 *     Kết quả về qua socket `zalo:connected` (list tự làm mới).
 *   - Bị đăng nhập nơi khác, hết phiên: ở dải cảnh báo trên ô soạn tin có thêm nút
 *     "Đăng nhập lại" → hiện QR ngay tại chỗ (không bay sang trang Tích hợp).
 *   - Kênh khác (OA/Facebook hết token), hoặc dùng ở bộ chọn tài khoản
 *     → sang trang Tích hợp đúng tab để cấp quyền lại.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, QrCode, RefreshCw, WifiOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { PLATFORM } from '@/lib/channel-groups'
import { useZaloLogin, useZaloReconnect, type ChannelStatus } from '@/hooks/use-integrations'
import { QrLoginDialog } from '@/pages/integrations/qr-login-dialog'
import { accountDownMessage, isAccountReconnecting } from '@/lib/account-status'

export interface ReconnectTarget {
  id: string
  platform: number
  displayName?: string | null
  liveStatus?: ChannelStatus | null
  isDisabled?: boolean
  /** Để nhận ra kênh ảo AI Sandbox (không bao giờ báo mất kết nối). */
  externalUid?: string | null
}

/** Tab của trang Tích hợp ứng với nền tảng. */
function integrationsTab(platform: number): string {
  switch (platform) {
    case PLATFORM.ZALO_OA: return 'zalo-oa'
    case PLATFORM.ZALO_BOT: return 'zalo-bot'
    case PLATFORM.TELEGRAM: return 'telegram-bot'
    case PLATFORM.TELEGRAM_BUSINESS: return 'telegram-app'
    case PLATFORM.FACEBOOK_PAGE: return 'facebook'
    case PLATFORM.PANCAKE_FB:
    case PLATFORM.PANCAKE_IG:
    case PLATFORM.PANCAKE_TIKTOK: return 'pancake'
    default: return 'zalo-personal'
  }
}

export function useReconnectAccount(onNeedQr?: (acc: ReconnectTarget) => void) {
  const { t } = useTranslation('chanCatalog')
  const navigate = useNavigate()
  const reconnect = useZaloReconnect()
  const goIntegrations = (platform: number) => navigate(`/integrations/${integrationsTab(platform)}`)
  // Zalo cá nhân + có chỗ hiện QR tại chỗ → mở QR; ngược lại về trang Tích hợp.
  const needQr = (acc: ReconnectTarget) => {
    if (onNeedQr && acc.platform === PLATFORM.ZALO_USER) onNeedQr(acc)
    else goIntegrations(acc.platform)
  }

  const run = (acc: ReconnectTarget) => {
    // Bị đá do đăng nhập nơi khác: dựng lại bằng phiên cũ sẽ đá ngược bên kia —
    // để người dùng tự quyết ở trang Tích hợp. Phiên hết hạn (token_expired): đã thử lại nhiều lần mà vẫn bị Zalo
    // từ chối → thử tiếp bằng phiên cũ vô ích, đi thẳng tới quét QR.
    if (acc.platform !== PLATFORM.ZALO_USER || acc.liveStatus === 'kicked' || acc.liveStatus === 'token_expired') {
      needQr(acc)
      return
    }
    reconnect.mutate(acc.id, {
      onSuccess: () =>
        toast.info(t('reconnect.reconnecting', { name: acc.displayName ?? 'Zalo' }), {
          description: t('reconnect.reconnectingDesc'),
          action: { label: t('reconnect.scanQr'), onClick: () => needQr(acc) },
        }),
      // Không còn phiên đã lưu / tài khoản đã ngắt → chỉ còn cách quét QR.
      onError: (err) => {
        toast.error(apiError(err))
        needQr(acc)
      },
    })
  }

  return { run, pendingId: reconnect.isPending ? reconnect.variables : undefined }
}

/** Nút nhỏ "Kết nối lại" — chặn sự kiện để không kích hoạt dòng chứa nó. */
export function ReconnectButton({
  account,
  className,
  size = 'xs',
  onNeedQr,
}: {
  account: ReconnectTarget
  className?: string
  size?: 'xs' | 'sm'
  /** Có → Zalo cá nhân cần quét QR sẽ gọi hàm này (hiện QR tại chỗ) thay vì chuyển trang. */
  onNeedQr?: (acc: ReconnectTarget) => void
}) {
  const { t } = useTranslation('chanCatalog')
  const { run, pendingId } = useReconnectAccount(onNeedQr)
  const busy = pendingId === account.id
  const stop = (e: React.SyntheticEvent) => { e.stopPropagation() }
  return (
    <Button
      type="button"
      variant="destructive"
      size="sm"
      disabled={busy}
      className={cn(size === 'xs' ? 'h-6 gap-1 px-2 text-[11px]' : 'h-8 gap-1.5 px-3 text-xs', className)}
      // Nằm trong DropdownMenuItem: không được để cú bấm chọn luôn tài khoản.
      onPointerDown={stop}
      onPointerUp={stop}
      onKeyDown={stop}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); run(account) }}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
      {t('reconnect.reconnect')}
    </Button>
  )
}

/**
 * Dải cảnh báo đặt ngay trên ô soạn tin của hội thoại.
 * Đang kết nối lại (zca-js tự thử lại) → vàng, lời nhẹ hơn; hỏng hẳn → đỏ.
 */
export function DisconnectedBanner({ account }: { account: ReconnectTarget }) {
  const { t } = useTranslation('chanCatalog')
  const soft = isAccountReconnecting(account)
  const isZaloPersonal = account.platform === PLATFORM.ZALO_USER
  const [qrOpen, setQrOpen] = useState(false)
  const login = useZaloLogin()
  // Bắt đầu phiên QR cho đúng tài khoản này rồi hiện hộp thoại QR ngay trên màn hội thoại.
  const startQr = () =>
    login.mutate(account.id, { onSuccess: () => setQrOpen(true), onError: (err) => toast.error(apiError(err)) })
  const { title, detail } = accountDownMessage(account)
  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 border-t px-4 py-2 text-sm',
        soft
          ? 'border-warning/30 bg-warning/10 text-warning'
          : 'border-destructive/30 bg-destructive/10 text-destructive',
      )}
    >
      {soft ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <WifiOff className="h-4 w-4 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title}</p>
        <p className="text-xs opacity-90">{detail}</p>
      </div>
      <ReconnectButton account={account} size="sm" onNeedQr={isZaloPersonal ? startQr : undefined} />
      {isZaloPersonal && (
        <>
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 bg-background px-3 text-xs text-foreground" disabled={login.isPending} onClick={startQr}>
            {login.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <QrCode className="h-3 w-3" />}
            {t('reconnect.loginAgain')}
          </Button>
          <QrLoginDialog accountId={account.id} open={qrOpen} onOpenChange={setQrOpen} />
        </>
      )}
    </div>
  )
}
